import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';
import { useState } from 'react';
import ExpandableContent from 'components/Generic/ExpandableContent';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { useI18n } from 'next-localization';
type SummaryProps = {
  onStepChange: (step: number) => void;
};
type FieldValue = {
  value: string;
  displayName: string;
};

type StepFields = {
  [fieldName: string]: FieldValue;
};
type DisplayData = [stepName: string, fields: StepFields][];

const SummaryRow: React.FC<{ question: FieldValue }> = ({ question }) => (
  <tr key={question.displayName} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.3)' }}>
    <td className="h6 w-50 py-4">{question.displayName}</td>
    <td className="body-default w-50 py-4">{question.value?.toString() || '-'}</td>
  </tr>
);

const Summary = ({ onStepChange }: SummaryProps) => {
  const { summary } = useFormContext();
  const { t } = useI18n();
  const [accordionToShow, setAccordionToShow] = useState<number | null>(0);
  const displayData: DisplayData = Object.entries(
    // @ts-expect-error TODO seems to be a bug: substracting two objects from each other `a - b`
    Object.fromEntries(Object.entries(summary).sort(([, a], [, b]) => a.index - b.index))
  ) as DisplayData;

  const printPDF = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.print();
  };

  return (
    <div className="col-8 print-container">
      {displayData.map(([step, data], accordionIndex) => (
        <div
          key={accordionIndex}
          className={`accordion border-bottom ${+accordionIndex !== 0 ? 'border-top' : ''}`}
        >
          <button
            className="btn-naked d-flex justify-content-between align-items-center w-100 py-3 gap-1"
            aria-expanded={accordionToShow === +accordionIndex ? 'true' : 'false'}
            onClick={() =>
              +accordionIndex === accordionToShow
                ? setAccordionToShow(null)
                : setAccordionToShow(accordionIndex)
            }
          >
            <span className="h3">{step}</span>
            <svg
              className="accordion__icon"
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M24 7.40068V5L12.8565 16.0448L12.0086 16.9101L11.1435 16.0448L0.0171306 5.01697L0 7.38371L12 19.2853L24 7.40068Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <ExpandableContent
            show={accordionIndex === accordionToShow}
            className="accordion-content"
          >
            <div className="accordion__panel d-flex flex-column flex-md-row justify-content-between">
              <div className="accordion__panel__inner">
                <table aria-label="Summary data" className="w-100">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.3)' }}>
                      <th className="w-50 py-4">Question</th>
                      <th className="w-50 py-4">Your answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data)
                      .filter(([, question]) => !!question.displayName && !!question.value)
                      .map(([, question]) => (
                        <SummaryRow key={question.displayName} question={question} />
                      ))}
                  </tbody>
                </table>
                <div className="left-side d-flex justify-content-between justify-content-md-start mt-2 mt-md-0">
                  <AbrdnButton
                    buttonType="secondary"
                    className="mt-5 text-link d-flex align-items-center"
                    replaceWithSlot={false}
                    showExternalIcon={false}
                    onClick={() => onStepChange(accordionIndex + 1)}
                  >
                    {t('edit')}
                  </AbrdnButton>
                  <div className="right-side">
                    <AbrdnButton
                      buttonType="secondary"
                      className="mt-5 text-link d-flex align-items-center print"
                      onClick={printPDF}
                    >
                      {t('print')}
                    </AbrdnButton>
                  </div>
                </div>
              </div>
            </div>
          </ExpandableContent>
        </div>
      ))}
    </div>
  );
};

export default Summary;
